/* The day, as one timeline you can work from.
 *
 * What separates this from a list of blocks on a page:
 *
 *   · **You can move between days.** It only ever showed today, which for a
 *     planner is a strange thing to withhold — "what does tomorrow look like"
 *     is the second question anybody asks.
 *
 *   · **Actions land instantly and can be taken back.** Ticking something off
 *     used to wait for a round trip before anything moved. Now the block goes
 *     immediately and the toast offers Undo; if the server refuses, the block
 *     comes back and says why. That is the difference between an app that
 *     feels like a form and one that feels like a tool.
 *
 *   · **It has a keyboard.** Every-day tools earn one.
 *
 * The blocks still are not draggable. `PUT /plan` uses optimistic concurrency
 * on a version, and a drag that ignores that would silently lose an edit made
 * on another device — worse than not having drag.
 */

import Dialog from '../ui/Dialog';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Block, Plan, Task } from '@shared/types';
import { get, post, patch, del, errorMessage } from '../api';
import { useSession, localToday } from '../session';
import ViewSwitch from '../ui/ViewSwitch';
import { useToast } from '../ui/toast';
import { useShortcuts } from '../ui/keys';
import { hhmm, human, nowMinutesIn, shiftDate, friendlyDate, relativeLabel } from '../ui/time';
import { ScheduleDialog, ConfirmDialog } from '../ui/schedule';
import AddToCalendar from '../ui/AddToCalendar';
import PlanAhead from '../ui/PlanAhead';
import FriendNudge from '../ui/FriendNudge';
import { readContext } from '../ui/dayContext';
import { useBlockDrag } from '../ui/dragBlock';
import ReplanDialog from '../ui/ReplanDialog';
import { startTour } from '../ui/Tour';
import type { Shortcut } from '../ui/keys';
import Icon from '../ui/Icon';
import type { IconName } from '../ui/Icon';

/* A picture per kind, beside the word rather than instead of it: the word is
   how somebody learns what the picture means, and after a week the picture is
   all they read. */
const KIND_ICON: Record<string, IconName> = {
  focus: 'focus', event: 'meeting', break: 'coffee',
  ritual: 'repeat', travel: 'travel', reward: 'gift'
};

const KIND_LABEL: Record<string, string> = {
  focus: 'Focus', event: 'Meeting', break: 'Break',
  ritual: 'Yours', travel: 'Travel', reward: 'For you'
};

/* The clock, the calendar and the words for both now live in ui/time.ts —
   they were copied into the next screen that needed them, which is how two
   views of the same day end up disagreeing about what 90 minutes is called. */

type Phase = 'past' | 'now' | 'next' | 'later';

/** Morning, Afternoon, Evening — the words people already use for when
    something can happen, and the landmarks a long timeline needs to be read at
    a glance rather than in order. Night is anything before the day starts;
    a night shift is a real working pattern and calling 03:00 "morning" would
    be wrong for the people who work it. */
function partOfDay(startMin: number): string {
  if (startMin < 5 * 60) return 'Night';
  if (startMin < 12 * 60) return 'Morning';
  if (startMin < 17 * 60) return 'Afternoon';
  if (startMin < 22 * 60) return 'Evening';
  return 'Night';
}

/* ── now, or next ──────────────────────────────────────────────
   Three states and one sentence each: something is happening (and ends in N
   minutes), nothing is but something starts soon (in N minutes), or the day's
   plan is behind you. The buttons carry their own names — "Finish now: …" —
   so they are not a second, identical "Done" beside the one on the block. */
function NowCard({ now, next, nowMin, doneTaskIds, runningTaskId, timerBusy, onDone, onStart, onStop, left }: {
  now: Block | null;
  next: Block | null;
  nowMin: number;
  doneTaskIds: Set<string>;
  runningTaskId: string | null;
  timerBusy: boolean;
  onDone: (b: Block) => void;
  onStart: (b: Block) => void;
  onStop: () => void;
  left: number;
}) {
  const b = now ?? next;

  if (!b) {
    return (
      <section className="now-card is-finished" aria-label="The rest of today">
        <span className="now-icon" aria-hidden="true"><Icon name="check2" size={22} /></span>
        <div className="now-body">
          <span className="now-label">Today</span>
          <strong className="now-title">Nothing else is planned for today</strong>
          <span className="muted small">The evening is yours.</span>
        </div>
      </section>
    );
  }

  const isNow = b === now;
  const done = !!b.taskId && doneTaskIds.has(b.taskId);
  const running = !!b.taskId && runningTaskId === b.taskId;
  const minutes = isNow ? b.endMin - nowMin : b.startMin - nowMin;
  const kind = b.apptId && b.kind === 'event' ? 'calendar' : KIND_ICON[b.kind] ?? 'focus';

  return (
    <section className={`now-card${isNow ? ' is-now' : ' is-next'}${running ? ' is-running' : ''}`}
             aria-label={isNow ? 'Happening now' : 'Up next'}>
      <span className="now-icon" aria-hidden="true"><Icon name={kind} size={22} /></span>

      <div className="now-body">
        <span className="now-label">
          {isNow ? (running ? 'Now · timing' : 'Now') : 'Up next'}
        </span>
        <strong className="now-title">{b.title}</strong>
        <span className="now-when">
          <Icon name="clock" size={13} />
          {b.start || hhmm(b.startMin)}–{b.end || hhmm(b.endMin)}
          <span className="now-count">
            {isNow ? `ends in ${human(Math.max(1, minutes))}` : `starts in ${human(Math.max(1, minutes))}`}
          </span>
          {left > 0 && <span className="muted"> · {human(left)} left today</span>}
        </span>
        {isNow && (
          <span className="now-progress" aria-hidden="true">
            <i style={{ width: `${Math.min(100, Math.max(0, ((nowMin - b.startMin) / Math.max(1, b.endMin - b.startMin)) * 100))}%` }} />
          </span>
        )}
      </div>

      <div className="now-actions">
        {b.joinUrl && (
          <a className="btn btn-primary" href={b.joinUrl} target="_blank" rel="noreferrer noopener">
            <Icon name="video" size={16} />Join
          </a>
        )}
        {b.taskId && !done && (running ? (
          <button className="btn" onClick={onStop} disabled={timerBusy}
                  aria-label={`Stop timing: ${b.title}`}>
            <Icon name="stop" size={15} />Stop
          </button>
        ) : (
          <button className="btn" onClick={() => onStart(b)} disabled={timerBusy || !!runningTaskId}
                  aria-label={`Time it: ${b.title}`}>
            <Icon name="play" size={15} />{isNow ? 'Start' : 'Start early'}
          </button>
        ))}
        {b.taskId && !done && (
          <button className="btn btn-primary" onClick={() => onDone(b)}
                  aria-label={`Finish now: ${b.title}`}>
            <Icon name="check" size={16} />Done
          </button>
        )}
        {done && <span className="chip chip-done"><Icon name="check" size={12} />done</span>}
      </div>
    </section>
  );
}

export default function TodayScreen() {
  const { today, user, loading } = useSession();
  const qc = useQueryClient();
  const toast = useToast();

  const realToday = today ?? localToday(user?.tz);

  /* Which day is on screen, as an *override* rather than a copy.
  
     Holding the date in state initialised from `realToday` reintroduced the
     timezone bug in a new place: the initial value is captured on the first
     render, when the session has not loaded and `realToday` is still the
     browser's UTC day, and the query fired with it before the effect could
     correct it. Deriving it instead means there is no window in which the two
     disagree.
  
     It also gets the midnight behaviour for free. Nothing overridden means the
     view follows the real day when it rolls over; having navigated somewhere
     deliberately means it stays put, which is right — moving somebody's view
     out from under them at midnight is worse than a stale label. */
  /* …and it lives in the URL rather than only in state, now that the month
     view can send you here. A day you opened from a month grid should be a day
     you can bookmark, reload, and come back to with the browser's own Back
     button — which means the address bar has to know which day it is. */
  const [params, setParams] = useSearchParams();
  const asked = params.get('date');
  const override = asked && /^\d{4}-\d{2}-\d{2}$/.test(asked) ? asked : null;
  const date = override ?? realToday;
  const isToday = date === realToday;

  const goToDate = (next: string | null) =>
    setParams(next && next !== realToday ? { date: next } : {}, { replace: true });

  const shiftBy = (days: number) => goToDate(shiftDate(override ?? realToday, days));

  const [nowMin, setNowMin] = useState(() => nowMinutesIn(user?.tz));
  useEffect(() => {
    setNowMin(nowMinutesIn(user?.tz));
    const t = setInterval(() => setNowMin(nowMinutesIn(user?.tz)), 60_000);
    return () => clearInterval(t);
  }, [user?.tz]);

  const [addOpen, setAddOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  /* Which block a dialog is asking about, and what it is asking. Placing
     something new is `{ kind: 'new' }` — the same dialog, with nothing to
     move yet. */
  const [asking, setAsking] = useState<
    { kind: 'move' | 'remove' | 'new'; block?: Block } | null>(null);
  const [replanning, setReplanning] = useState(false);

  /* Dragging on the day.
   *
   * The day is a list rather than a grid — deliberately, because a list can
   * carry the actions, the reasons and the now-line that a positioned column
   * cannot. So the drop target is not the list: a rail of the day's hours
   * appears beside it while a drag is in progress, and disappears afterwards.
   * The alternative was to rebuild the day as a positioned column and lose
   * everything that makes it readable, in order to gain a gesture. */
  const drag = useBlockDrag({
    onMove: (id, at) => {
      const b = plan.data?.blocks.find(x => x.id === id);
      if (!b || at.startMin === b.startMin) return;
      moveTo.mutate({ id, startMin: at.startMin, title: b.title, from: b });
    }
  });
  const nowRef = useRef<HTMLLIElement | null>(null);
  /* `complete` is declared before `reopen` and its toast needs to call it, so
     the reference is late-bound. One reversal path, used by both the toast and
     the button on the block. */
  const reopenRef = useRef<((b: Block) => void) | null>(null);

  /* The hours the rail covers: the working day, widened to hold whatever is
     actually on the day, with an hour of air either side. Wide enough to drop
     into an evening, narrow enough that a quarter of an hour is a real target. */
  const railTop = Math.max(0, Math.min(user?.workStart ?? 540, 8 * 60) - 60);
  const railBottom = Math.min(1440, Math.max(user?.workEnd ?? 1080, 20 * 60) + 60);
  const railSpan = Math.max(120, railBottom - railTop);
  const railHours = Array.from(
    { length: Math.floor(railSpan / 60) + 1 },
    (_, i) => Math.ceil(railTop / 60) * 60 + i * 60
  ).filter(m => m < railBottom);

  const planKey = ['plan', date] as const;

  const plan = useQuery({
    queryKey: planKey,
    /* Not until the session has settled: before this the screen fetched on its
       first render, when the date still fell back to the browser's UTC day. */
    enabled: !loading,
    queryFn: async () => {
      const r = await get<{ plan: Plan }>(`/api/plan?date=${date}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not load your day.'));
      return r.plan;
    }
  });

  /* The day does not know which of its blocks are finished — a block carries a
     taskId, and doneness lives on the task. Without this the screen cannot
     distinguish work you have done from work you have not, which is most of
     what a timeline is for. */
  const tasks = useQuery({
    queryKey: ['tasks'],
    enabled: !loading,
    queryFn: async () => {
      const r = await get<{ tasks: Task[] }>('/api/tasks');
      return r.ok ? r.tasks : [];
    }
  });
  const doneTaskIds = useMemo(
    () => new Set((tasks.data ?? []).filter(t => t.done).map(t => t.id)),
    [tasks.data]);

  const timer = useQuery({
    queryKey: ['timer'],
    enabled: !loading,
    queryFn: async () => {
      const r = await get<{ running: { taskId?: string | null } | null }>('/api/timer');
      return r.ok ? r.running : null;
    }
  });

  const refreshDay = () => {
    void qc.invalidateQueries({ queryKey: ['plan'] });
    void qc.invalidateQueries({ queryKey: ['balance'] });
    void qc.invalidateQueries({ queryKey: ['timer'] });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['month'] });
    void qc.invalidateQueries({ queryKey: ['rewards'] });
  };

  /* ── moving things by hand ────────────────────────────────────
     The planner places; you rearrange. Both go through the same endpoint, so
     a block you moved obeys the same rules as one the solver placed — sleep
     stays protected and a clash is refused by name rather than silently
     overwriting somebody's meeting. */
  async function moveBlock(block: Block, when: { date: string; startMin: number; minutes: number }) {
    const r = await patch(`/api/plan/block/${block.id}`, when);
    if (!r.ok) throw new Error(errorMessage(r, 'Could not move it.'));
    refreshDay();
    toast.show({
      message: when.date === date
        ? `Moved “${block.title}” to ${hhmm(when.startMin)}.`
        : `Moved “${block.title}” to ${friendlyDate(when.date)}.`,
      tone: 'good',
      /* A move is as easy to get wrong as a tick, and just as symmetric to
         reverse: the same request, with where it was. */
      action: { label: 'Undo', run: () => { void putBack(block, date); } }
    });
  }

  /* Where a block was, restored through the same endpoint that moved it —
     so sleep and clashes are checked on the way back too, and a refusal says
     why rather than silently leaving it where it went. */
  async function putBack(block: Block, onDate: string) {
    const r = await patch(`/api/plan/block/${block.id}`, {
      date: onDate, startMin: block.startMin, minutes: block.endMin - block.startMin
    });
    refreshDay();
    if (!r.ok) toast.say(errorMessage(r, 'Could not put it back.'), 'bad');
    else toast.say(`“${block.title}” is back at ${hhmm(block.startMin)}.`);
  }


  /* One request, the same one the Move dialog makes. */
  const moveTo = useMutation({
    mutationFn: async ({ id, startMin }: { id: string; startMin: number; title: string; from: Block }) => {
      const r = await patch(`/api/plan/block/${id}`, { date, startMin });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not move it.'));
      return startMin;
    },
    onSuccess: (startMin, v) => {
      refreshDay();
      toast.show({
        message: `Moved “${v.title}” to ${hhmm(startMin)}.`,
        tone: 'good',
        action: { label: 'Undo', run: () => { void putBack(v.from, date); } }
      });
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  /* Who may see one block, said in three steps: what its category says, only
     that you are busy, nobody. A block can say less than its category and
     never more — the server enforces that, this only asks. */
  const PRIVACY_NEXT = { none: 'busy', busy: 'hidden', hidden: null } as const;
  const PRIVACY_SAID = {
    busy: 'Other people now see only that you are busy then.',
    hidden: 'Nobody else can see this one now.',
    none: 'Back to what its category shares.'
  } as const;
  const setPrivacy = useMutation({
    mutationFn: async (block: Block) => {
      const next = PRIVACY_NEXT[block.privacy ?? 'none'];
      const r = await patch(`/api/plan/block/${block.id}`, { privacy: next });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change who sees it.'));
      return next;
    },
    onSuccess: next => { refreshDay(); toast.say(PRIVACY_SAID[next ?? 'none']); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const removeBlock = useMutation({
    mutationFn: async (block: Block) => {
      const r = await del(`/api/plan/block/${block.id}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not take it off.'));
      return block;
    },
    onSuccess: b => {
      refreshDay();
      toast.show({
        message: b.taskId
          ? `“${b.title}” is off the day and back in your tasks.`
          : `“${b.title}” is off the day.`,
        /* Taking something off is the one action on the day that loses where
           it was. Put back the same thing at the same time, for the same task,
           rather than asking first. */
        action: {
          label: 'Undo',
          run: () => {
            void post('/api/plan/block', {
              date,
              startMin: b.startMin,
              minutes: b.endMin - b.startMin,
              title: b.title,
              ...(b.taskId ? { taskId: b.taskId } : { kind: b.kind, typeId: b.typeId ?? null })
            }).then(r => {
              refreshDay();
              if (!r.ok) toast.say(errorMessage(r, 'Could not put it back.'), 'bad');
            });
          }
        }
      });
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  /* The document title is the app's label in a row of twenty tabs. A planner
     that says the same thing on every day of the week wastes it. */
  useEffect(() => {
    const label = isToday ? 'Today' : friendlyDate(date);
    document.title = `${label} · Amon`;
    return () => { document.title = 'Amon'; };
  }, [date, isToday]);

  /* ── ticking something off ────────────────────────────────────
     Optimistic, because the answer is never in doubt from the user's side:
     they know they finished it. Waiting a round trip to acknowledge that is
     the single most noticeable bit of lag in the app.

     The cached day is edited directly and the previous copy kept, so a
     refusal restores exactly what was there rather than triggering a refetch
     that might arrive with somebody else's concurrent change folded in. */
  const complete = useMutation({
    mutationFn: async (block: Block) => {
      if (!block.taskId) throw new Error('That block is not a task, so there is nothing to tick off.');
      const r = await patch(`/api/tasks/${block.taskId}`, { done: true });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not tick that off.'));
      return block;
    },
    onMutate: async (block: Block) => {
      /* Marked done on the *task*, not removed from the day.
      
         Removing the block was the obvious optimistic edit and it was wrong:
         the server keeps the block — finishing something does not un-plan the
         hour you spent on it — so the refetch put it straight back and the
         user watched their work reappear a second after ticking it off.
      
         Editing the same cache the server will confirm means the optimistic
         state and the settled state agree, which is the whole point. */
      await qc.cancelQueries({ queryKey: ['tasks'] });
      const previous = qc.getQueryData<Task[]>(['tasks']);
      qc.setQueryData<Task[]>(['tasks'], old =>
        old?.map(t => (t.id === block.taskId ? { ...t, done: true } : t)));
      return { previous };
    },
    onError: (e: Error, _block, ctx) => {
      if (ctx?.previous) qc.setQueryData(['tasks'], ctx.previous);
      toast.say(e.message, 'bad');
    },
    onSuccess: block => {
      void qc.invalidateQueries({ queryKey: ['balance'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.show({
        message: `Done: ${block.title}`,
        tone: 'good',
        /* An accidental tick is the most likely mistake on this screen, and
           the reversal is exactly symmetric — so offering it afterwards beats
           a confirmation before. */
        action: { label: 'Undo', run: () => { reopenRef.current?.(block); } }
      });
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['tasks'] }); }
  });

  /* The inverse of Done, reachable from the block itself as well as from the
     toast. The toast is the fast path and it expires; this is the one that is
     still there tomorrow when you notice you ticked the wrong thing. */
  const reopen = useMutation({
    mutationFn: async (block: Block) => {
      const r = await patch(`/api/tasks/${block.taskId}`, { done: false });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not put that back.'));
      return block;
    },
    onMutate: async (block: Block) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      const previous = qc.getQueryData<Task[]>(['tasks']);
      qc.setQueryData<Task[]>(['tasks'], old =>
        old?.map(t => (t.id === block.taskId ? { ...t, done: false } : t)));
      return { previous };
    },
    onError: (e: Error, _b, ctx) => {
      if (ctx?.previous) qc.setQueryData(['tasks'], ctx.previous);
      toast.say(e.message, 'bad');
    },
    onSuccess: b => { refreshDay(); toast.say(`Back on the list: ${b.title}`); },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['tasks'] }); }
  });

  const startTimer = useMutation({
    mutationFn: async (block: Block) => {
      const r = await post('/api/timer/start', { taskId: block.taskId, blockId: block.id });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not start the timer.'));
      return block;
    },
    onSuccess: b => { refreshDay(); toast.say(`Timing ${b.title}.`); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const stopTimer = useMutation({
    mutationFn: async () => {
      const r = await post<{ session?: { minutes?: number } }>('/api/timer/stop');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not stop the timer.'));
      return r;
    },
    onSuccess: r => {
      refreshDay();
      const mins = r.session?.minutes;
      toast.say(mins ? `Stopped — ${human(mins)} recorded.` : 'Timer stopped.', 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  reopenRef.current = (b: Block) => reopen.mutate(b);

  const blocks = plan.data?.blocks ?? [];
  const dayCtx = readContext(plan.data?.context);

  const phases = useMemo(() => {
    const map = new Map<string, Phase>();
    if (!isToday) return map;
    let nextFound = false;
    for (const b of blocks) {
      const key = b.id ?? `${b.startMin}`;
      if (b.endMin <= nowMin) map.set(key, 'past');
      else if (b.startMin <= nowMin) map.set(key, 'now');
      else if (!nextFound) { map.set(key, 'next'); nextFound = true; }
      else map.set(key, 'later');
    }
    return map;
  }, [blocks, nowMin, isToday]);

  const nowIndex = useMemo(() => {
    if (!isToday || !blocks.length) return -1;
    const i = blocks.findIndex(b => b.endMin > nowMin);
    return i === -1 ? blocks.length : i;
  }, [blocks, nowMin, isToday]);

  /* Scroll to where you are, once per day viewed. Opening a day at 4pm and
     being shown 9am is a small daily annoyance. */
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!plan.data || !isToday || scrolledFor.current === date) return;
    scrolledFor.current = date;
    const el = nowRef.current;
    /* Guarded: an effect that throws unmounts the tree, and scrolling to the
       right place is a nicety while showing the day at all is not. */
    if (el && typeof el.scrollIntoView === 'function') {
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* nicety */ }
    }
  }, [plan.data, isToday, date]);

  const shape = useMemo(() => {
    const work = blocks.filter(b => b.kind !== 'break');
    const planned = work.reduce((a, b) => a + (b.endMin - b.startMin), 0);
    /* Counted apart, because they are different sentences. Four meetings is a
       day somebody else filled; an appointment is a day you are partly not
       here for. Calling the second one a meeting is the app describing your
       life back to you in its own vocabulary. */
    const meetings = blocks.filter(b => b.kind === 'event' && !b.apptId).length;
    const appts = blocks.filter(b => b.apptId && b.kind === 'event').length;
    const left = isToday
      ? work.filter(b => b.endMin > nowMin)
          .reduce((a, b) => a + (b.endMin - Math.max(b.startMin, nowMin)), 0)
      : 0;
    return { planned, meetings, appts, left };
  }, [blocks, nowMin, isToday]);

  const running = timer.data;

  const shortcuts = useMemo<Shortcut[]>(() => [
    { key: 'ArrowLeft', label: 'Previous day', group: 'Moving around',
      run: () => shiftBy(-1) },
    { key: 'ArrowRight', label: 'Next day', group: 'Moving around',
      run: () => shiftBy(1) },
    { key: 't', label: 'Jump back to today', group: 'Moving around',
      run: () => goToDate(null) },
    { key: 'n', label: 'Add something to the backlog', group: 'Doing',
      run: () => setAddOpen(true) },
    { key: 'p', label: 'Put something on the day at a time', group: 'Doing',
      run: () => setAsking({ kind: 'new' }) },
    { key: 'r', label: 'Replan this day', group: 'Doing',
      run: () => setReplanning(true) },
    { key: 's', label: 'Stop the running timer', group: 'Doing',
      run: () => { if (running) stopTimer.mutate(); } },
    { key: '?', label: 'Show this list', group: 'Help',
      run: () => setHelpOpen(o => !o) },
    /* `t` is already "jump back to today", so the tour gets `g` for guide.
       The tour's own closing line names this key, and the two must agree. */
    { key: 'g', label: 'Take the tour again', group: 'Help',
      run: () => { setHelpOpen(false); startTour(); } },
    { key: 'Escape', label: 'Close what is open', group: 'Help',
      run: () => { setHelpOpen(false); setAddOpen(false); } }
  ], [realToday, running, stopTimer]);

  useShortcuts(shortcuts);

  return (
    <div className="screen">
      <ViewSwitch />

      <header className="topbar">
        <div className="daynav">
          <button className="btn btn-ghost btn-icon" aria-label="Previous day"
                  title="Previous day" onClick={() => shiftBy(-1)}><Icon name="left" /></button>
          <div>
            <h1>{isToday ? 'Today' : friendlyDate(date, true)}</h1>
            <p className="muted">
              {isToday ? friendlyDate(date) : relativeLabel(date, realToday)}
            </p>
            {/* The day's shape as three glanceable numbers. Each keeps its
                words: an icon beside "4h 45m planned" is recognised faster,
                and the words are what a screen reader and a newcomer read. */}
            {shape.planned > 0 && (
              <p className="day-stats">
                <span className="stat"><Icon name="clock" size={14} />{human(shape.planned)} planned</span>
                {shape.meetings > 0 && (
                  <span className="stat"><Icon name="meeting" size={14} />
                    {shape.meetings} meeting{shape.meetings === 1 ? '' : 's'}</span>
                )}
                {shape.appts > 0 && (
                  <span className="stat"><Icon name="calendar" size={14} />
                    {shape.appts} appointment{shape.appts === 1 ? '' : 's'}</span>
                )}
                {isToday && shape.left > 0 && (
                  <span className="stat stat-accent"><Icon name="play" size={12} />{human(shape.left)} ahead</span>
                )}
              </p>
            )}
          </div>
          <button className="btn btn-ghost btn-icon" aria-label="Next day"
                  title="Next day" onClick={() => shiftBy(1)}><Icon name="right" /></button>
          {!isToday && (
            <button className="btn btn-ghost btn-sm" onClick={() => goToDate(null)}>
              <Icon name="today" size={16} />Today
            </button>
          )}
        </div>

        <div className="inline">
          {/* It proposes now rather than writing. The day is the person's, and
              the cheapest moment to disagree with a plan is before it exists. */}
          <button className="btn btn-primary" onClick={() => setReplanning(true)}
                  title="Let Amon rearrange the rest of the day">
            <Icon name="sparkle" size={16} />Replan
          </button>
          <button className="btn btn-ghost btn-icon kbd-only" onClick={() => setHelpOpen(true)}
                  aria-label="Keyboard shortcuts" title="Keyboard shortcuts">
            <Icon name="keyboard" />
          </button>

        </div>
      </header>

      {running && (
        <div className="notice notice-strong running-bar">
          <span className="running-what"><Icon name="clock" size={16} />
            Timing <strong>{runningTitle(running, blocks)}</strong></span>
          <button className="btn btn-ghost btn-sm" onClick={() => stopTimer.mutate()}
                  disabled={stopTimer.isPending}><Icon name="stop" size={14} />Stop</button>
        </div>
      )}

      {/* The one thing to look at. Opening the day at 14:20 should answer
          "what am I doing right now, and what do I press" without reading a
          timeline — so the block under the now-line is lifted out, with its
          actions made big, and the timeline stays below for everything else. */}
      {isToday && plan.data && blocks.length > 0 && (
        <NowCard
          now={blocks.find(b => phases.get(b.id ?? `${b.startMin}`) === 'now') ?? null}
          next={blocks.find(b => phases.get(b.id ?? `${b.startMin}`) === 'next') ?? null}
          nowMin={nowMin}
          doneTaskIds={doneTaskIds}
          runningTaskId={running?.taskId ?? null}
          timerBusy={startTimer.isPending || stopTimer.isPending}
          onDone={b => complete.mutate(b)}
          onStart={b => startTimer.mutate(b)}
          onStop={() => stopTimer.mutate()}
          left={shape.left} />
      )}

      {/* Only when the days ahead are genuinely empty — see PlanAhead. Shown on
          the day because that is the view from which an empty fortnight is
          invisible. */}
      {isToday && <PlanAhead today={realToday} />}

      {/* Somebody you care about is overdue and there is a free evening soon. */}
      {isToday && <FriendNudge today={realToday} />}

      {/* Both ways of adding, in one row: the backlog (Amon finds the time) and
          a time you chose. They were two stacked strips, and two strips read as
          two unrelated features rather than one decision. */}
      <div className="inline wrap day-add">
        <QuickAdd
          open={addOpen}
          onOpen={() => setAddOpen(true)}
          onClose={() => setAddOpen(false)}
          onAdded={t => { refreshDay(); toast.say(`Added “${t}”. Press Replan to place it.`, 'good'); }} />
        <button className="btn btn-ghost btn-sm day-add-at"
                aria-label="Put something at a time" title="Put something at a time"
                onClick={() => setAsking({ kind: 'new' })}>
          {/* The words go on a phone, where the row has room for one field and
              an icon; the name stays for screen readers either way. */}
          <Icon name="clock" size={16} /><span className="day-add-label">Put something at a time</span> <kbd>p</kbd>
        </button>
      </div>

      {(loading || plan.isPending) && !plan.isError && (
        <ol className="timeline" aria-busy="true" aria-label="Loading your day">
          {[0, 1, 2].map(i => <li key={i} className="block block-skeleton" />)}
        </ol>
      )}

      {plan.isError && (
        <div className="notice notice-bad" role="alert">
          <p><strong>Could not load your day.</strong></p>
          <p className="muted small">{(plan.error as Error).message}</p>
          <button className="btn btn-ghost btn-sm" onClick={() => plan.refetch()}>Try again</button>
        </div>
      )}

      {!dayCtx.blocked && dayCtx.label && (
        <div className="notice day-context" role="note">
          <span className="notice-icon"><Icon name="flag" size={16} /></span>
          <p>{dayCtx.label}</p>
        </div>
      )}

      {dayCtx.blocked && (
        <div className="notice notice-strong">
          {dayCtx.reason || dayCtx.label} — nothing new is placed on a day
          your own calendar says you are away.
        </div>
      )}

      {plan.data && blocks.length === 0 && (
        <div className="empty empty-day">
          <span className="empty-icon" aria-hidden="true"><Icon name="sun" size={28} /></span>
          <h2>{isToday ? 'Your day is clear' : 'Nothing planned'}</h2>
          <p className="muted">
            Tell Amon what you owe and it finds the hours — or put something at a time you chose.
          </p>
          <div className="empty-actions">
            <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
              <Icon name="plus" size={16} />Add a task
            </button>
            <button className="btn" onClick={() => setAsking({ kind: 'new' })}>
              <Icon name="clock" size={16} />Put something at a time
            </button>
          </div>
        </div>
      )}

      {/* The rail. Present only during a drag, because a column of hours
          beside a list is clutter the rest of the time — and it is what makes
          "drop it at half past two" mean something on a screen that is
          otherwise a list. */}
      {drag.drag && (
        <div className="drop-rail" aria-hidden="true"
             data-drop-date={date}
             data-top-min={railTop}
             data-span-min={railSpan}>
          {railHours.map(m => (
            <span key={m} className="drop-rail-hour"
                  style={{ top: `${((m - railTop) / railSpan) * 100}%` }}>{hhmm(m)}</span>
          ))}
          {drag.drag.at && (
            <span className="drop-rail-at"
                  style={{ top: `${((drag.drag.at.startMin - railTop) / railSpan) * 100}%` }}>
              {hhmm(drag.drag.at.startMin)}
            </span>
          )}
        </div>
      )}

      {plan.data && blocks.length > 0 && (
        <ol className="timeline">
          {blocks.map((b, i) => {
            const key = b.id ?? `${b.startMin}-${i}`;
            const phase = phases.get(b.id ?? `${b.startMin}`) ?? 'later';
            const prev = blocks[i - 1];
            const gap = prev ? b.startMin - prev.endMin : 0;
            const isRunning = !!running && !!b.taskId && running.taskId === b.taskId;
            const isDone = !!b.taskId && doneTaskIds.has(b.taskId);

            return (
              <li key={key} ref={phase === 'now' || phase === 'next' ? nowRef : undefined}>
                {/* Which part of the day this is. A timeline with fourteen
                    rows and no landmarks is a list of times you have to read
                    in order; three headings turn it into a shape you can see
                    at a glance — and they are the words people already use
                    when they say when something can happen. */}
                {partOfDay(b.startMin) !== (prev ? partOfDay(prev.startMin) : null) && (
                  <h2 className="day-part">{partOfDay(b.startMin)}</h2>
                )}

                {/* After the heading, not before it: the line marks where you
                    are inside a part of the day, so it belongs under the name
                    of that part. */}
                {i === nowIndex && <NowLine at={nowMin} />}

                {/* Free time is information, not absence. A 90-minute hole
                    between two meetings is the answer to "when could I do
                    this", and it used to render as nothing at all. */}
                {gap >= 15 && (
                  <div className="gap"><span>{human(gap)} free</span></div>
                )}

                {/* Labelled as one thing, so a screen reader announces
                    "09:00 to 10:30, Write the memo, focus" rather than
                    reading four unrelated fragments in whatever order the
                    markup happens to put them. */}
                <article
                  aria-label={`${b.start || hhmm(b.startMin)} to ${b.end || hhmm(b.endMin)}, ` +
                    `${b.title}, ${b.apptId && b.kind === 'event' ? 'appointment' : (KIND_LABEL[b.kind] ?? b.kind).toLowerCase()}` +
                    `${isDone ? ', done' : ''}${phase === 'now' ? ', happening now' : ''}`}
                  className={`block block-${b.kind} is-${phase}`
                  + (isRunning ? ' is-running' : '') + (isDone ? ' is-done' : '')
                  + (b.id && !b.apptId ? ' is-movable' : '')
                  + (drag.drag?.id === b.id ? ' is-dragging' : '')}
                  onPointerDown={e => {
                    /* An appointment's blocks are rebuilt from the appointment
                       itself, so a drag here would be undone by the next
                       change to it. */
                    if (b.id && !b.apptId) drag.start(e, b.id, b.startMin);
                  }}
                  onPointerMove={drag.move}
                  onPointerUp={drag.end}
                  onPointerCancel={drag.cancel}>
                  {/* Both ends of it, not just the start. "09:00, 90m" is
                      two numbers and a subtraction; "09:00–10:30" is when
                      you are free again, which is the thing being asked. */}
                  <div className="block-time">
                    <span>{b.start || hhmm(b.startMin)}</span>
                    <span className="block-end">{b.end || hhmm(b.endMin)}</span>
                    <em>{human(Math.max(1, b.endMin - b.startMin))}</em>
                  </div>

                  <div className="block-body">
                    <div className="block-head">
                      <strong>{b.title}</strong>
                      {/* An appointment says so. It is an `event` block like a
                          meeting is, and calling a dentist appointment a
                          meeting is the app describing your life back to you
                          in its own vocabulary. */}
                      <span className="chip chip-kind">
                        <Icon name={b.apptId && b.kind === 'event' ? 'calendar' : KIND_ICON[b.kind] ?? 'focus'}
                              size={12} />
                        {b.apptId && b.kind === 'event'
                          ? 'Appointment'
                          : KIND_LABEL[b.kind] ?? b.kind}
                      </span>
                      {b.locked && <span className="chip chip-soft"><Icon name="pin" size={12} />pinned</span>}
                      {b.privacy && (
                        <span className="chip chip-soft">
                          <Icon name={b.privacy === 'hidden' ? 'lock' : 'eye'} size={12} />
                          {b.privacy === 'hidden' ? 'private' : 'busy only'}
                        </span>
                      )}
                      {isDone && <span className="chip chip-done"><Icon name="check" size={12} />done</span>}
                      {phase === 'now' && <span className="chip chip-now">now</span>}
                      {phase === 'next' && <span className="chip chip-soft">next up</span>}
                    </div>

                    {b.why && <p className="muted small">{b.why}</p>}

                    <div className="block-actions">
                      {b.taskId && !isDone && (
                        <button className="btn btn-ghost btn-icon btn-good" onClick={() => complete.mutate(b)}
                                aria-label="Done" title="Mark done">
                          <Icon name="check" />
                        </button>
                      )}
                      {b.taskId && isDone && (
                        <button className="btn btn-ghost btn-icon"
                                onClick={() => reopen.mutate(b)}
                                aria-label="Not done" title="Not done after all"><Icon name="undo" /></button>
                      )}
                      {b.taskId && !isDone && !isRunning && !running && (
                        <button className="btn btn-ghost btn-icon"
                                onClick={() => startTimer.mutate(b)}
                                disabled={startTimer.isPending}
                                aria-label="Start" title="Start the timer"><Icon name="play" /></button>
                      )}
                      {isRunning && (
                        <button className="btn btn-ghost btn-icon"
                                onClick={() => stopTimer.mutate()}
                                disabled={stopTimer.isPending}
                                aria-label="Stop" title="Stop the timer"><Icon name="stop" /></button>
                      )}
                      {b.joinUrl && (
                        <a className="btn btn-ghost btn-sm" href={b.joinUrl}
                           target="_blank" rel="noreferrer noopener">
                          <Icon name="video" size={16} />Join {b.joinProvider ?? 'call'}
                        </a>
                      )}
                      {/* An appointment's blocks are not edited here: they are
                          rebuilt from the appointment, so a move made on this
                          screen would be undone the next time it changed. The
                          appointment itself is where they are edited. */}
                      {b.id && !b.apptId && (
                        <>
                          <button className="btn btn-ghost btn-icon"
                                  onClick={() => setAsking({ kind: 'move', block: b })}
                                  aria-label="Move" title="Move to another time">
                            <Icon name="move" />
                          </button>
                          <button className="btn btn-ghost btn-icon"
                                  onClick={() => setPrivacy.mutate(b)}
                                  disabled={setPrivacy.isPending}
                                  aria-label={`Who sees this: ${b.privacy === 'hidden' ? 'nobody'
                                    : b.privacy === 'busy' ? 'only that you are busy' : 'as its category says'}`}
                                  title="Who sees this — tap to change">
                            <Icon name={b.privacy === 'hidden' ? 'lock' : 'eye'} />
                          </button>
                          <button className="btn btn-ghost btn-icon btn-bad"
                                  onClick={() => setAsking({ kind: 'remove', block: b })}
                                  aria-label="Take off" title="Take it off the day">
                            <Icon name="trash" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </article>
              </li>
            );
          })}
          {nowIndex === blocks.length && <li><NowLine at={nowMin} done /></li>}
        </ol>
      )}

      {asking?.kind === 'move' && asking.block && (
        <ScheduleDialog
          title="Move it"
          what={asking.block.title}
          description="Sleep is still protected, and anything already at that time will say so."
          initial={{
            date,
            startMin: asking.block.startMin,
            minutes: asking.block.endMin - asking.block.startMin
          }}
          confirmLabel="Move it"
          onClose={() => setAsking(null)}
          onSubmit={when => moveBlock(asking.block!, when)} />
      )}

      {asking?.kind === 'new' && (
        <AddToCalendar
          date={date}
          startMin={Math.min(1380, Math.max(nowMin, 8 * 60))}
          onClose={() => setAsking(null)}
          onAdded={(what, repeat, moved) => {
            refreshDay();
            void qc.invalidateQueries({ queryKey: ['rituals'] });
            void qc.invalidateQueries({ queryKey: ['year'] });
            toast.say(
              repeat === 'weekly' ? `“${what}” is held every week now.`
                : repeat === 'yearly' ? `“${what}” comes round every year now.`
                : moved ? `“${what}” is on ${friendlyDate(date)} — ${moved} thing${moved === 1 ? '' : 's'} moved later.`
                : `“${what}” is on ${friendlyDate(date)}.`, 'good');
          }} />
      )}

      {asking?.kind === 'remove' && asking.block && (
        <ConfirmDialog
          title={`Take “${asking.block.title}” off the day?`}
          description={asking.block.taskId
            ? 'The task goes back to your backlog, so Amon can place it again — or you can.'
            : 'It comes off this day. Nothing else changes.'}
          confirmLabel="Take it off"
          onClose={() => setAsking(null)}
          onConfirm={() => removeBlock.mutateAsync(asking.block!)} />
      )}

      {replanning && (
        <ReplanDialog
          date={date}
          today={realToday}
          onClose={() => setReplanning(false)}
          onApplied={placed => {
            refreshDay();
            toast.say(placed
              ? `Placed ${placed} thing${placed === 1 ? '' : 's'}.`
              : 'Nothing new to place.', placed ? 'good' : 'info');
          }} />
      )}

      {helpOpen && <ShortcutHelp shortcuts={shortcuts} onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function runningTitle(running: { taskId?: string | null }, blocks: Block[]): string {
  const hit = blocks.find(b => b.taskId && b.taskId === running.taskId);
  return hit ? hit.title : 'something';
}

function NowLine({ at, done = false }: { at: number; done?: boolean }) {
  return (
    <div className="now-line" role="separator"
         aria-label={done ? 'Everything planned is behind you' : `Now, ${hhmm(at)}`}>
      <span>{done ? `${hhmm(at)} · nothing left planned` : hhmm(at)}</span>
    </div>
  );
}

/* ── capture without losing your place ───────────────────────────
   The thing you remember mid-morning is the thing that never gets written
   down, because writing it down meant navigating away from the day. */
function QuickAdd({ open, onOpen, onClose, onAdded }: {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onAdded: (title: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [mins, setMins] = useState('30');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const add = useMutation({
    mutationFn: async () => {
      const r = await post('/api/tasks', {
        title: title.trim(), estMin: Math.max(5, Math.min(600, Number(mins) || 30))
      });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not add that.'));
      return title.trim();
    },
    onSuccess: t => {
      onAdded(t);
      setTitle('');
      setError(null);
      /* Stays open: people add two or three things in a row, and closing the
         box after each one turns that into three round trips through a
         button. */
      inputRef.current?.focus();
    },
    onError: (e: Error) => setError(e.message)
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('What is it?'); return; }
    add.mutate();
  }

  if (!open) {
    return (
      <button className="quickadd-open" onClick={onOpen}>
        <Icon name="plus" size={16} />Add something to today <kbd>n</kbd>
      </button>
    );
  }

  return (
    <form className="quickadd" onSubmit={submit}>
      <input
        ref={inputRef} value={title} maxLength={200}
        placeholder="What do you need to do?"
        onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') { onClose(); setError(null); } }} />
      <input type="number" min="5" max="600" step="5" aria-label="Minutes"
             value={mins} onChange={e => setMins(e.target.value)} />
      <span className="muted small">min</span>
      <button className="btn btn-primary btn-sm" type="submit" disabled={add.isPending}>
        {add.isPending ? 'Adding…' : 'Add'}
      </button>
      <button className="btn btn-ghost btn-sm" type="button"
              onClick={() => { onClose(); setError(null); }}>Cancel</button>
      {error && <p className="auth-err" role="alert">{error}</p>}
    </form>
  );
}

/* ── the shortcut list ───────────────────────────────────────────
   Generated from the same array that binds the keys, so the help cannot drift
   from the behaviour. A shortcut nobody can discover is dead code. */
function ShortcutHelp({ shortcuts, onClose }: { shortcuts: Shortcut[]; onClose: () => void }) {
  /* Through the shared Dialog, so Escape, the focus trap and focus coming back
     are the same here as in every other dialog — this was the one modal left
     that had its own idea of all three. */
  const groups = shortcuts.reduce<Record<string, Shortcut[]>>((acc, s) => {
    (acc[s.group] ||= []).push(s);
    return acc;
  }, {});

  const pretty = (k: string) =>
    k === 'ArrowLeft' ? '←' : k === 'ArrowRight' ? '→' : k === 'Escape' ? 'Esc' : k;

  return (
    <Dialog title="Keyboard shortcuts" icon="keyboard" onClose={onClose}>
      {/* Discoverable rather than only remembered: somebody who has forgotten
          what a screen does looks for help, not for a tour. */}
      <button className="btn btn-ghost btn-sm shortcut-tour"
              onClick={() => { onClose(); startTour(); }}>
        <Icon name="play" size={14} />Take the tour again
      </button>
      {Object.entries(groups).map(([group, list]) => (
        <div key={group} className="shortcut-group">
          <h3 className="muted small">{group}</h3>
          {list.map(s => (
            <div key={s.key} className="shortcut-row">
              <kbd>{pretty(s.key)}</kbd>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      ))}
    </Dialog>
  );
}
